import type {
  Accent,
  Additional,
  Background,
  Category,
  Core,
  DeepGrey,
  DeepPurple,
  Disabled,
  Error,
  Ghost,
  Green,
  Icons,
  Orange,
  Overlay,
  Primary,
  Purple,
  Red,
  Secondary,
  Success,
  Tertiary,
  Text,
  Theme,
  Transparent,
  Turquoise,
  Warning,
} from './themes'

export const additional = {
  accentElement: '#ebecee',
} as const satisfies Additional

export const background = {
  main: '#ffffff',
  accent: '#f5f6f7',
  colorAccent: '#4209870A', // '#420A87, transparent 4%',
} as const satisfies Background

export const disabled = {
  main: '#f1eff5',
  accent: '#d5d2d7',
  lowContrast: '#c9c2ca33',
  mediumContrast: '#c2bcc6',
} as const satisfies Disabled

export const error = {
  main: '#9a0000',
  mediumContrast: '#f0bfbf',
  lowContrast: '#f9e5e8',
} as const satisfies Error

export const ghost = {
  main: '#766478',
  mediumContrast: '#9c919e',
  lowContrast: '#f1eff5',
} as const satisfies Ghost

export const icons = {
  main: '#6f5d71',
  logo: '#450d87',
} as const satisfies Icons

export const overlay = {
  main: '#978b99',
  accent: '#1A0826B8', // '#1A0826, transparent 72%',
  tooltip: '#1A0826B8', // '#1A0826, transparent 72%',
} as const satisfies Overlay

export const primary = {
  maxContrast: '#2a0c55',
  highContrast: '#450d87',
  upperMidContrast: '#7035c2',
  midContrast: '#ae8ee4',
  lowerMidContrast: '#cec1e1',
  lowContrast: '#e1dbee',
  reversedContrast: '#ffffff',
  // legacy aliases
  mainContrast: '#450d87',
  mediumContrast: '#cec1e1',
} as const satisfies Primary

export const tertiary = {
  maxContrast: '#16071f',
  highContrast: '#514454',
  upperMidContrast: '#6f5d71',
  midContrast: '#7d707f',
  lowerMidContrast: '#c2bcc6',
  lowContrast: '#c9c2ca33',
  reversedContrast: '#ffffff',
  // legacy aliases
  main: '#514454',
  upperMediumContrast: '#6f5d71',
  mediumContrastAlternative: '#7d707f',
  mediumContrast: '#DEDBE0',
} as const satisfies Tertiary

export const secondary = {
  maxContrast: '#053d3c',
  highContrast: '#0b7573',
  upperMidContrast: '#1bbab7',
  midContrast: '#55cfcc',
  lowerMidContrast: '#c6efee',
  lowContrast: '#e3f7f7',
  reversedContrast: '#ffffff',
  // legacy aliases (mainContrast was #00a5a1 — now the DS high-contrast shade)
  mainContrast: '#0b7573',
  mediumContrast: '#c6efee',
} as const satisfies Secondary

export const accent = {
  maxContrast: '#2a0c55',
  highContrast: '#450d87',
  lowerMidContrast: '#cec1e1',
  lowContrast: '#e1dbee',
  surfaceElement: '#ebecee',
} as const satisfies Accent

export const success = {
  main: '#4e6c35',
  mediumContrast: '#b5d3a3',
  lowContrast: '#e2ecd9',
} as const satisfies Success

export const warning = {
  main: '#cb7007',
  mediumContrast: '#f5ddc3',
  lowContrast: '#faead8',
} as const satisfies Warning

export const text = {
  title: '#16071f',
  body: '#514454',
  description: '#6f5d71',
  caption: '#978b99',
  titleReversed: '#ffffff',
  disabled: '#978b99',
  emptyState: '#7d707f',
} as const satisfies Text

export const transparent = {
  background: '#FFFFFF40', // #ffffff, transparent 25%
  main: '#ffffff',
} as const satisfies Transparent

export const deepPurple = {
  100: '#F1EFF5',
  200: '#D5D2D7',
  300: '#9C919E',
  600: '#7D707F',
  700: '#766478',
  800: '#514454',
  900: '#16071F',
  transparent900: '#1A0826B8', // 1A0826, 72% transparent
} as const satisfies DeepPurple

export const deepGrey = {
  100: '#928f8f1f',
  200: '#5c5c5c3d',
  300: '#878383',
  600: '#5c5c5c',
  700: '#7b7b7b',
  800: '#b3b3b3',
  900: '#e6e6e6',
  transparent900: '#e6e6e6b8', // 1A0826, 72% transparent
} as const satisfies DeepGrey

export const turquoise = {
  100: '#E3F7F7',
  200: '#C6EFEE',
  400: '#55CFCC',
  500: '#00A5A1',
} as const satisfies Turquoise

export const green = {
  100: '#EDFFF0',
  200: '#A0AD97',
  500: '#4E6C35',
} as const satisfies Green
export const orange = {
  100: '#FAEAD8',
  200: '#F0C493',
  500: '#CB7007',
} as const satisfies Orange

export const red = {
  100: '#F9E5E8',
  200: '#F0BFBF',
  500: '#9A0000',
} as const satisfies Red

export const purple = {
  50: '#4209870A', // 420A87, 4% transparent
  100: '#E1DBEE',
  200: '#CEC1E1',
  400: '#450D87',
  500: '#2A0C55',
} as const satisfies Purple

export const core = {
  black: '#0A0C0C',
  white: '#FFFFFF',
  grey: '#E1E4E6',
  lightGrey: '#F5F6F7',
  transparentWhite: '#FFFFFF40', // FFFFFF, transparent white 25%
  transparentBlack: '#121214CC', // 131315, transparent black 80%
} as const satisfies Core

export const category = {
  cash: {
    main: '#00BF60',
    background: '#D9EFE0',
  },
  deals: {
    main: '#33BBFF',
    background: '#DFEEF4',
  },
  electronics: {
    main: '#00BFAF',
    background: '#D9EFEA',
  },
  entertainment: {
    main: '#BFBF00',
    background: '#EFEFD5',
  },
  fashion: {
    main: '#FF3333',
    background: '#F7DEDB',
  },
  financial: {
    main: '#FF3366',
    background: '#F7DEE1',
  },
  foodDrinks: {
    main: '#80BF00',
    background: '#E8EFD5',
  },
  groceries: {
    main: '#00BF60',
    background: '#D9EFE0',
  },
  health: {
    main: '#00BF10',
    background: '#D9EFD7',
  },
  houseGarden: {
    main: '#8000BF',
    background: '#E8D8EC',
  },
  householdBill: {
    main: '#FF3333',
    background: '#F7DEDB',
  },
  kids: {
    main: '#7733FF',
    background: '#E7DEF4',
  },
  onlineShopping: {
    main: '#4433FF',
    background: '#E1DEF4',
  },
  others: {
    main: '#828B99',
    background: '#E8E8E7',
  },
  pets: {
    main: '#D99F2B',
    background: '#F3EBDA',
  },
  sport: {
    main: '#FF6633',
    background: '#F7E4DB',
  },
  subscription: {
    main: '#FF3366',
    background: '#F7DEE1',
  },
  transfers: {
    main: '#3388FF',
    background: '#DFE8F4',
  },
  travel: {
    main: '#3355FF',
    background: '#DFE2F4',
  },
  transport: {
    main: '#FF9933',
    background: '#F7EADB',
  },
} as const satisfies Category

export const palette = {
  additional,
  background,
  disabled,
  error,
  ghost,
  icons,
  overlay,
  primary,
  tertiary,
  secondary,
  accent,
  success,
  text,
  transparent,
  warning,
  deepPurple,
  turquoise,
  green,
  orange,
  red,
  purple,
  core,
  category,
  deepGrey,
} as const satisfies Theme
